
import { SubscriptionStatus } from "./types";

export const getDefaultSubscriptionStatus = (): SubscriptionStatus => ({
  subscribed: false,
  onTrial: false,
  active: false,
  subscriptions: [],
  loading: true,
  error: null
});

/**
 * Check if a subscription check should be deferred based on timing
 */
export const shouldDeferCheck = (
  now: number,
  lastCheck: number,
  inProgress: boolean
): boolean => {
  // Always defer if a check is already in progress
  if (inProgress) return true;
  
  // Defer if the last check was recent (within last 2 seconds)
  const minTimeBetweenChecks = 2000; // 2 seconds
  return now - lastCheck < minTimeBetweenChecks;
};

/**
 * Safely determine if a user has an active subscription, handling API edge cases
 */
export const determineSubscriptionStatus = (status: SubscriptionStatus): boolean => {
  // If we encounter an API error, default to providing access
  // This is a graceful degradation to prevent blocking paid users
  if (status.error) {
    console.warn("Using fallback subscription access due to error:", status.error);
    return true;
  }
  
  // First check explicit active flag
  if (status.active) {
    return true;
  }
  
  // Then check for trial status
  if (status.onTrial) {
    return true;
  }
  
  // Finally, check for any active subscriptions in the array
  if (status.subscriptions && status.subscriptions.length > 0) {
    const hasActiveOrTrial = status.subscriptions.some(sub => 
      sub.status === 'active' || 
      sub.onTrial || 
      (sub.trialEnd && new Date(sub.trialEnd) > new Date())
    );
    
    if (hasActiveOrTrial) {
      return true;
    }
  }
  
  return false;
};

/**
 * Safe JSON parsing with fallbacks - enhanced to handle various data formats
 * This is crucial for proper security type handling
 */
export const safeJsonParse = (data: any): any => {
  if (!data) {
    console.log("safeJsonParse: No data provided");
    return null;
  }
  
  // Already an object
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    console.log("safeJsonParse: Data is already an object, returning as is");
    return data;
  }
  
  // String that needs parsing
  if (typeof data === 'string') {
    console.log("safeJsonParse: Attempting to parse string:", data.substring(0, 100) + (data.length > 100 ? "..." : ""));
    try {
      const parsed = JSON.parse(data);
      console.log("safeJsonParse: Successfully parsed JSON:", typeof parsed);
      return parsed;
    } catch (e) {
      console.warn("safeJsonParse: Failed to parse JSON string:", e);
      
      // Try to clean the string and parse again
      try {
        // Sometimes strings are double-encoded or have escaped quotes
        if (data.startsWith('"') && data.endsWith('"')) {
          const inner = data.slice(1, -1).replace(/\\"/g, '"');
          console.log("safeJsonParse: Trying to parse quoted string:", inner.substring(0, 100));
          return JSON.parse(inner);
        }
        
        // Another common format is escaped JSON
        if (data.includes('\\\"')) {
          const cleaned = data.replace(/\\"/g, '"');
          console.log("safeJsonParse: Trying to parse escaped string:", cleaned.substring(0, 100));
          return JSON.parse(cleaned);
        }
      } catch (innerError) {
        console.warn("safeJsonParse: Failed cleanup parse attempt:", innerError);
      }
    }
  }
  
  console.log("safeJsonParse: Could not parse data, returning null");
  return null;
};
